import React, {useState} from 'react'
import styled from 'styled-components'
import RowSpacer from './styles/RowSpacer'
import OAuthConfig from '../config/OAuthConfig'
import BooksitoutImages from '../images/BooksitoutImages'
import {useDarkMode} from "./hooks/DarkModeContext";
import {authenticateWithPasskey, isPasskeySupported} from '../utils/passkey';
import toast from "react-hot-toast";
import useLoginStore from '../routes/login/useLoginStore';

interface Props {
    message: string | null
}

const Login: React.FC<Props> = ({message}) => {
    const {isDark} = useDarkMode()
    const [isPasskeyLoading, setIsPasskeyLoading] = useState(false)
    const {login} = useLoginStore()

    const handlePasskeyLogin = async () => {
        if (!isPasskeySupported()) {
            toast.error('이 기기에서는 Passkey를 지원하지 않아요.')
            return
        }

        setIsPasskeyLoading(true)
        try {
            const result = await authenticateWithPasskey()
            if (result.success && result.loginData) {
                login(result.loginData)

                if (result.loginData.name === '') {
                    toast(`어서오세요, 다시 만나서 반가워요!`, { icon: '✋' })
                } else {
                    toast(`어서오세요, ${result.loginData.name}님!`, { icon: '✋' })
                }
            } else {
                toast.error('Passkey 로그인 중 오류가 발생했습니다.')
            }
        } catch (error) {
            toast.error('Passkey 로그인 중 오류가 발생했습니다.')
        } finally {
            setIsPasskeyLoading(false)
        }
    }

    return (
        <NoLoginContainer>
            <Title>{message ?? '10초만에 로그인해 이용하기'}</Title>
            <RowSpacer/>

            <OAuthContainer>
                {OAuthConfig.map(oauth => (
                    <a
                        href={oauth.redirectUrl}
                        style={{pointerEvents: !oauth.active ? 'none' : 'auto'}}
                        onClick={(e) => {
                            e.preventDefault()
                            localStorage.setItem('redirect-url', window.location.pathname + window.location.search)
                            window.location.href = oauth.redirectUrl
                        }}
                    >
                        <OAuthButton src={isDark ? oauth.imageDark : oauth.image} alt={`${oauth.displayName}`}
                                     active={oauth.active}/>
                    </a>
                ))}
            </OAuthContainer>
            <RowSpacer size={10}/>

            <PasskeyButton onClick={handlePasskeyLogin} disabled={isPasskeyLoading || !isPasskeySupported()}>
                {isPasskeyLoading ? (
                    <>Passkey로 로그인 중...</>
                ) : isPasskeySupported() ? (
                    <>Passkey로 로그인하기</>
                ) : (
                    <></>
                )}
            </PasskeyButton>
            <RowSpacer size={10}/>

            <ImageContainer>
                <Image src={BooksitoutImages.Illustration.relax} alt="login"/>
            </ImageContainer>
            <RowSpacer/>

            <p className={'p-0 m-0'} style={{color: 'var(--sub-text-color)'}}>
                책잇아웃에 로그인하면{' '}
                <a href={'https://booksitout.com/privacy/2023-07-01?language=KOREAN'} className={'text-book fw-bold'}>
                    개인정보처리방침
                </a>
                에 동의한 것으로 간주돼요.
                <div className={'d-none d-md-block'}></div> 왜 아이디/비밀번호 로그인이 없는지는{' '}
                <a href={'https://booksitout.com/faq#1?language=KOREAN'} className={'text-book fw-bold'}>
                    자주묻는질문
                </a>
                을 참고해 주세요.
            </p>
        </NoLoginContainer>
    )
}

const NoLoginContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`

const Title = styled.h1`
    color: var(--text-color);
    font-size: 35px;
    font-weight: 700;
    padding-top: 3px;
`

const OAuthContainer = styled.div`
    text-align: center;
`

interface OAuthButtonProps {
    active: boolean
}

const OAuthButton = styled.img.attrs(props => ({
    // @ts-ignore
    className: `img-fluid ms-2 me-2 ms-md-3 me-md-3 rounded ${!props.active && 'opacity-50'} hover-effect`,
}))<OAuthButtonProps>`
    width: 50px;
`

const ImageContainer = styled.div`
    min-height: 300px;
    justify-content: center;
    text-align: center;
`

const Image = styled.img.attrs({
    className: '',
})`
    height: 100%;
    width: 100%;
`

const PasskeyButton = styled.button.attrs({
    className: 'hover-effect'
})`
    color: var(--text-color);
    border: none;
    border-radius: 12px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    margin: 10px 0;
    min-width: 200px;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`

export default Login
